"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";

export const WAVE_RUN_MS = 4500;

/** The hero wave is decoration: it runs for under 5 seconds, and not at all under reduced motion. */
export function waveKeepsRunning(elapsedMs: number, reduceMotion: boolean) {
  return !reduceMotion && elapsedMs < WAVE_RUN_MS;
}

export default function AudioWave() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size to match its display size
    const resize = () => {
      const { width, height } = canvas.getBoundingClientRect();
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
    };

    // Initial resize
    resize();
    window.addEventListener("resize", resize);

    // Wave parameters with brand colors
    const waves = [
      {
        frequency: 0.02,
        amplitude: 30,
        speed: 0.03,
        offset: 0,
        color: "#091661",
      }, // clarity-depth
      {
        frequency: 0.03,
        amplitude: 20,
        speed: 0.02,
        offset: 5,
        color: "#232F72",
      }, // insight-indigo
      {
        frequency: 0.01,
        amplitude: 40,
        speed: 0.01,
        offset: 10,
        color: "#9DA2C0",
      }, // coverage-core
    ];

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const startedAt = performance.now();
    let animationId = 0;
    let time = 0;

    const paint = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      waves.forEach((wave) => {
        ctx.beginPath();
        ctx.strokeStyle = wave.color;
        ctx.lineWidth = 2.5;

        for (let x = 0; x < canvas.width; x++) {
          const y =
            canvas.height / 2 +
            Math.sin(x * wave.frequency + time * wave.speed + wave.offset) *
              wave.amplitude;

          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.stroke();
      });
    };

    // WCAG 2.2.2: the loop ends on its own, and the last frame stays on the canvas.
    const tick = () => {
      paint();
      if (!waveKeepsRunning(performance.now() - startedAt, reduceMotion)) return;
      time += 1;
      animationId = requestAnimationFrame(tick);
    };

    tick();
    // A resize clears the canvas, so draw the current frame again.
    window.addEventListener("resize", paint);

    // Cleanup
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("resize", paint);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <div className="py-6 md:py-8">
      <canvas
        ref={canvasRef}
        className="w-full h-24 md:h-32"
        style={{
          opacity: 0.8,
        }}
        aria-label="Animated audio wave visualization representing real-time health insurance processing"
      />
      <noscript>
        <Image
          src="/images/audio-wave-fallback.svg"
          alt="Audio wave visualization showing dynamic health insurance processing capabilities - multiple overlapping sine waves representing real-time data analysis and cost-sharing calculations"
          width={1200}
          height={160}
          className="w-full h-24 md:h-32 object-contain"
          style={{ opacity: 0.7 }}
          priority
        />
      </noscript>
    </div>
  );
}
